<script>
	import thumb_OVpTGuisUlU from '$lib/Components/Youtube/images/OVpTGuisUlU.jpg?w=610';

	import Bubble from '$lib/Components/Layout/Bubble/Bubble.svelte';
	import Grid from '$lib/Components/Layout/Grid/Grid.svelte';
	import Image from '$lib/Components/Layout/Image/Image.svelte';
	import Section from '$lib/Components/Layout/Section/Section.svelte';
	import Youtube from '$lib/Components/Youtube/Youtube.svelte';
	import GetAFreeEstimate from '$lib/PageSections/GetAFreeEstimate/GetAFreeEstimate.svelte';

	import exteriorStormWindowInstallationImg from './images/exteriorStormWindowInstallation.jpg';
	import indowWindowInsertStormWindowsImg from './images/indowWindowInsertStormWindows.jpg';
	import metalAndGlassStormWindowImg from './images/metalAndGlassStormWindow.png';
	import metalFoamAndAcrylicImg from './images/metalFoamAndAcrylic.png';
	import plasticAndAcrylicImg from './images/plasticAndAcrylic.png';
	import plasticAndGlassStormWindowImg from './images/plasticAndGlassStormWindow.png';
	import siliconeAndAcrylicImg from './images/siliconeAndAcrylic.png';
	import windowInsertAndStormWindowImg from './images/windowInsertAndStormWindow.png';
	import woodAndGlassStormWindowImg from './images/woodAndGlassStormWindow.png';
</script>

<Section>
	<Image
		src={windowInsertAndStormWindowImg}
		alt="indow window Interior vs exterior storm windows"
	/>
	<p>
		Both types of storm windows – interior and exterior – help with window
		insulation and energy efficiency. They add a layer to your window to help
		regulate temperature, damp noise, and protect your existing window. If
		you’re still considering replacement windows, read our comparison: <a
			href="/resources/storm-windows-guide/replacement"
			>Storm Windows vs Replacement Windows</a
		>.
	</p>
	<p>
		Interior and exterior windows have a lot of similarities. It’s in
		understanding their differences, such as inside window insulation, that will
		help you decide which is best for your home or building.
	</p>
	<nav class="flex justify-center">
		<ul class="flex list-none flex-col items-center">
			<li><a href="#materials">Storm Window Materials</a></li>
			<li><a href="#performance">Storm Window Performance</a></li>
			<li><a href="#appearance">Storm Window Appearance</a></li>
			<li><a href="#installation">Storm Window Installation</a></li>
			<li><a href="#chart">Summary Chart</a></li>
			<li><a href="#cost">Storm Window Cost</a></li>
			<li><a href="#AERC Certification">AERC Certification</a></li>
		</ul>
	</nav>
</Section>
<Section
	backgroundColor="grey-light"
	title="What are storm windows made of?"
	id="materials"
>
	<p>
		There are many storm window styles. Which storm window style you choose
		depends on: your home, existing windows, and what issues you are dealing
		with.
	</p>
	<p>
		<strong>Exterior</strong>: Glass panels surrounded by wood or aluminum
		frame. These attach to the outside of your window frame using screws, caulk,
		or hinges. Sometimes the frame is semi-permanent so the pane will be
		released by swivel pins for cleaning or replacement while the frame stays
		attached to the house. Most have weep holes so accumulated moisture can
		evaporate.
	</p>
	<Grid columns={3} rows={2} center class="gap-x-12! gap-y-0!">
		<Grid subgrid vertical>
			<Image
				src={metalAndGlassStormWindowImg}
				alt="Interior vs exterior storm windows"
				class="
      w-72
      md:w-48
    "
			/>
			<p>Metal & Glass</p>
		</Grid>

		<Grid subgrid vertical>
			<Image
				src={woodAndGlassStormWindowImg}
				alt="Interior vs exterior storm windows"
				class="
      w-72
      md:w-48
    "
			/>
			<p>Metal & Glass</p>
		</Grid>

		<Grid subgrid vertical>
			<Image
				src={plasticAndGlassStormWindowImg}
				alt="Interior vs exterior storm windows"
				class="
      w-72
      md:w-48
    "
			/>
			<p>Metal & Glass</p>
		</Grid>
	</Grid>

	<p>
		<strong>Interior</strong>: Interior storm windows are panels made of glass,
		vinyl, or acrylic surrounded by a frame of wood, metal, plastic, tape, or
		rubber. They attach to the inside of your window frame through magnets,
		adhesive, a track system, or compression system.
	</p>

	<Grid columns={3} rows={2} center class="gap-x-12! gap-y-0!">
		<Grid subgrid vertical>
			<Image
				src={siliconeAndAcrylicImg}
				alt="Interior vs exterior storm windows"
				class="
      w-72
      md:w-48
    "
			/>
			<p>Silicone & Acrylic</p>
		</Grid>

		<Grid subgrid vertical>
			<Image
				src={metalFoamAndAcrylicImg}
				alt="Interior vs exterior storm windows"
				class="
      w-72
      md:w-48
    "
			/>
			<p>Metal & Acrylic</p>
		</Grid>

		<Grid subgrid vertical>
			<Image
				src={plasticAndAcrylicImg}
				alt="Interior vs exterior storm windows"
				class="
      w-72
      md:w-48
    "
			/>
			<p>Plastic & Acrylic</p>
		</Grid>
	</Grid>
	<p>
		<strong>Interior storm window benefits</strong>: Invisible from the outside,
		no weep holes for higher efficiency
	</p>
</Section>

<Section id="performance">
	<h2>Storm window performance against air leaks & noise.</h2>
	<p>
		<strong>Exterior</strong>: Exterior storm windows protect your windows and
		increase energy efficiency. Because of their weep holes, they cannot achieve
		a near airtight seal. This means they cannot stop all air and noise from
		getting in (where air goes, noise goes).
	</p>
	<p>
		<strong>Interior</strong>: Interior storm windows can create a near airtight
		seal with very little air leaking through. Many are custom made to fit your
		window snugly (which means non-standard shapes a no problem), blocking air
		from entering or exiting. This helps with window soundproofing. The dead air
		space created between the window and insert adds to insulation and
		soundproofing.
	</p>
	<p>
		<strong>Interior storm window benefits</strong>: Inside window insulation
		via dead air space, snug fit, custom shapes
	</p>
	<div class="flex w-full flex-col items-center gap-4">
		<h3>Video: Do Storm Windows Help Insulate?</h3>
		<Youtube
			posterImageSrc={thumb_OVpTGuisUlU}
			src="https://www.youtube.com/embed/OVpTGuisUlU"
			title="How to Insulate Windows | Indow"
		/>
	</div>
</Section>

<Section id="appearance">
	<Image
		src={indowWindowInsertStormWindowsImg}
		alt="indow window insert storm windows"
	/>
	<h2>How will storm windows alter my home’s appearance?</h2>
	<p>
		<strong>Exterior</strong>: Exterior storm windows can interfere with
		curbside appeal as they are always visible and can obscure shutters, sashes,
		casing—all meant to provide 3D effect and add character. They can sometimes
		be painted to match home’s exterior. Exterior storm windows are used to
		preserve old windows, but detract from the home’s face by covering up
		details that make it interesting.
	</p>
	<p>
		<strong>Interior</strong>: You cannot see most interior storm windows from
		the outside. Some you can notice from the inside, others disappear once they
		are in place. Is interior design important to you? Get interior window
		inserts that insulate your home while maintaining your aesthetic.
	</p>
	<p>
		<strong>Interior storm window benefits</strong>: Doesn’t change home’s
		appearance
	</p>
	<br />
</Section>

<Section id="installation">
	<h2>Storm window installation and maintenance.</h2>
	<p>
		<strong>Exterior</strong>: Exterior storm windows can be medium to difficult
		to install. There’s no need for carpentry skills (unless you build them
		yourself), but there is need for a ladder unless your windows are basement
		or low to the ground. You will also need some hardware and tools. They have
		permanent to semi-permanent application. To maintain, you’ll need to climb
		that ladder again and use tools.
	</p>
	<Image
		src={exteriorStormWindowInstallationImg}
		alt="indow window insert storm windows"
	/>
	<p>
		<strong>Interior</strong>: Interior storm windows can be installed without
		leaving your house. Some require semi-permanent hardware and tools to
		install. Some just push into place and can be removed and reinstalled
		easily.
	</p>
	<p>
		<strong>Interior storm window benefits</strong>: Can be quickly installed
		and removed
	</p>
</Section>

<Section backgroundColor="blue" id="chart">
	<h2>Interior vs exterior storm windows: Which are right for me?</h2>
	<p>
		Is noise an issue for you? Do you care about altering the appearance of your
		home? Is easy installation and maintenance important to you?
	</p>
	<table>
		<thead>
			<tr>
				<th></th>
				<th>Protect Windows</th>
				<th>Energy Efficiency</th>
				<th>Soundproof</th>
				<th>Doesn’t Alter Appearance</th>
				<th>Easy Installation</th>
				<th>Cost</th>
			</tr>
		</thead>
		<tbody>
			<tr>
				<td>
					<div class="justify-self-center font-bold text-indow-grey-dark">
						Exterior
					</div>
					<div class="justify-self-center font-bold text-indow-grey-dark">
						Interior
					</div>
				</td>

				<td data-cell="Protect Windows">
					<div class="flex w-full justify-center">
						<div class="circle aspect-square w-3 bg-indow-yellow"></div>
					</div>

					<div class="flex w-full justify-center">
						<div class="circle aspect-square w-3 bg-indow-yellow"></div>
					</div>
				</td>

				<td data-cell="Enery Efficiency">
					<div class="flex w-full justify-center">
						<div class="circle aspect-square w-3 bg-indow-yellow"></div>
					</div>

					<div class="flex w-full justify-center gap-x-1">
						<div class="circle aspect-square w-3 bg-indow-yellow"></div>
						<div class="circle aspect-square w-3 bg-indow-yellow"></div>
					</div>
				</td>

				<td data-cell="Soundproof">
					<div class="flex w-full justify-center">
						<div class="circle aspect-square w-3 bg-indow-yellow"></div>
					</div>

					<div class="flex w-full justify-center gap-x-1">
						<div class="circle aspect-square w-3 bg-indow-yellow"></div>
						<div class="circle aspect-square w-3 bg-indow-yellow"></div>
					</div>
				</td>

				<td data-cell="Doesn’t Alter Appearance">
					<div></div>

					<div class="flex w-full justify-center gap-x-1">
						<div class="circle aspect-square w-3 bg-indow-yellow"></div>
					</div>
				</td>

				<td data-cell="Easy Installation">
					<div></div>

					<div class="flex w-full justify-center gap-x-1">
						<div class="circle aspect-square w-3 bg-indow-yellow"></div>
					</div>
				</td>
				<td data-cell="Cost">
					<div
						class="
        flex w-full justify-center text-indow-yellow
        md:w-max
      "
					>
						$ - $$
					</div>

					<div
						class="
        flex w-full justify-center text-indow-yellow
        md:w-max
      "
					>
						$ - $$
					</div>
				</td>
			</tr>

			<tr>
				<td>
					<div
						class="flex w-full justify-center font-bold text-indow-grey-dark"
					>
						Interior
					</div>
				</td>
				<td data-cell="Protect Windows">
					<div class="flex w-full justify-center">
						<div class="circle aspect-square w-3 bg-indow-yellow"></div>
					</div>
				</td>
				<td data-cell="Energy Efficiency">
					<div class="flex w-full justify-center gap-x-1">
						<div class="circle aspect-square w-3 bg-indow-yellow"></div>
						<div class="circle aspect-square w-3 bg-indow-yellow"></div>
					</div>
				</td>
				<td data-cell="Soundproof">
					<div class="flex w-full justify-center gap-x-1">
						<div class="circle aspect-square w-3 bg-indow-yellow"></div>
						<div class="circle aspect-square w-3 bg-indow-yellow"></div>
					</div>
				</td>
				<td data-cell="Doesn’t Alter Appearance">
					<div class="flex w-full justify-center gap-x-1">
						<div class="circle aspect-square w-3 bg-indow-yellow"></div>
					</div>
				</td>
				<td data-cell="Easy Installation">
					<div class="flex w-full justify-center gap-x-1">
						<div class="circle aspect-square w-3 bg-indow-yellow"></div>
					</div>
				</td>
				<td data-cell="Cost">
					<div
						class="
        flex w-full justify-center gap-x-1 text-indow-yellow
        md:w-max
      "
					>
						$ - $$
					</div>
				</td>
			</tr>
		</tbody>
	</table>
</Section>

<Section id="cost">
	<h2>How much do storm windows cost?</h2>
	<Bubble title="Cost for both exterior and interior storm windows depends on:">
		<ul>
			<li>Construction & materials</li>
			<li>How much maintenance you want to put into it</li>
			<li>If they are historically accurate</li>
			<li>How long they last</li>
			<li>If they are custom made for you windows</li>
		</ul>
	</Bubble>
	<p>
		The best storm windows will take a larger investment upfront, but will have
		a higher and quicker return on investment. Also, look at how long each storm
		window will last. One may cost less upfront, but would need to be replaced
		every few years.
	</p>
	<Bubble
		title="For an average window, including labor and supplies, each type costs:"
	>
		<ul>
			<li><strong>Exterior storm windows</strong>: $164 – $287</li>
			<li><strong>Indow window inserts</strong>: $372 – $480</li>
			<li><strong>Traditional wood storm windows</strong>: $525 – $577</li>
		</ul>
	</Bubble>
</Section>

<Section id="AERC Certification">
	<h2>AERC Certification</h2>
	<p>
		Indow is AERC certified. AERC, the Attachments Energy Rating Council, is an
		unaffiliated organization that <a
			href="https://aercenergyrating.org/product-search/residential-product-search"
			rel="external"
			target="_blank">rates window attachment products on energy efficiency</a
		> by measuring various factors such as Air Leakage (AL) and U-Factor.
	</p>
	<p>
		On air leakage, Indow scored in the top two out of 33 storm windows tested,
		11 of which were interior storm windows. U-Factor is an indicator of heat
		transfer through a surface. It tells you how well a window product
		insulates. Indow inserts have a U-Factor of .3, making them a top performer
		on insulation among all available storm windows. Indow inserts are your
		solution for comfort, quiet, and energy savings.
	</p>
</Section>

<GetAFreeEstimate title="Let Us Help You" />

<style>
	table {
		border-collapse: collapse;
		overflow-x: scroll;
		width: 100%;
	}
	tr {
		background-color: var(--color-indow-blue);
	}

	tr:last-child {
		display: none;
	}

	@media screen and (min-width: 768px) {
		tr:last-child {
			display: table-row;
		}
	}

	th {
		padding: 1rem;
		display: none;
		background-color: var(--color-indow-grey);
		border: solid 1px white;
	}

	@media screen and (min-width: 768px) {
		th {
			display: table-cell;
		}
	}

	td {
		padding: 1rem;
		display: grid;
		grid-template-columns: 1fr 1fr 1fr;
		align-items: center;
		column-gap: 0.75rem;
		border: solid 1px white;
	}

	@media screen and (min-width: 768px) {
		td {
			display: table-cell;
		}

		td > *:nth-child(2n) {
			display: none;
		}
	}

	td::before {
		content: attr(data-cell);
		font-weight: 700;
		text-transform: capitalize;
	}

	@media screen and (min-width: 768px) {
		td::before {
			display: none;
		}
	}
</style>
